/**
 * LegalDocPage (T27.2.10.2) — the shared PUBLIC legal-document shell.
 *
 * Renders one published legal document (fetched at runtime from
 * `GET /api/legal/:slug` via features/legal/content) into accessible,
 * token-styled HTML, inside a self-contained public page chrome
 * (brand mark + "Back to app" link + the document body). It deliberately does
 * NOT use AppShell or any session-bound hook: these routes mount OUTSIDE
 * AuthGuard so a logged-out Play reviewer can deep-link them (the store-listing
 * Privacy Policy URL + the Data Safety Delete-account URL).
 *
 * Public surfaces in this folder:
 *   - LegalDocPage   — renders a single legal draft (privacy/terms/eula/cookies)
 *   - LegalIndexPage — the `/legal` index linking the set
 *   - DeleteAccountPage — the Data Safety wrapper (its own module)
 *
 * The page renders the PUBLISHED copy (the DB row written by an admin Publish),
 * fetched at runtime — so a content change ships without an app release. The
 * renderer never substitutes an unresolved `{{…}}` token — it renders verbatim
 * (see markdown.tsx).
 */

import type { CSSProperties, ReactNode } from 'react';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Logo } from '../../components';
import { routes } from '../../routes/paths';
import { fetchLegalDoc, LEGAL_DOCS, type LegalSlug } from './content';
import { renderLegalMarkdown } from './markdown';

// ---------------------------------------------------------------------------
// Token-only public-page chrome. Mirrors LoginPage's safe-area insets so the
// page clears the Android status bar / gesture-nav bar in the native WebView,
// and reads the theme-aware app background + text tokens.
// ---------------------------------------------------------------------------

const pageStyle: CSSProperties = {
  minHeight: '100vh',
  boxSizing: 'border-box',
  backgroundColor: 'var(--color-bg-primary)',
  color: 'var(--color-text-primary)',
  fontFamily: 'var(--font-family-sans)',
  padding: 'var(--space-6) var(--space-4)',
  paddingTop: 'max(var(--space-6), env(safe-area-inset-top, 0px))',
  paddingBottom: 'max(var(--space-8), env(safe-area-inset-bottom, 0px))',
};

const containerStyle: CSSProperties = {
  width: '100%',
  maxWidth: '44rem',
  marginLeft: 'auto',
  marginRight: 'auto',
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--space-4)',
};

const topbarStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 'var(--space-3)',
  flexWrap: 'wrap',
};

const backLinkStyle: CSSProperties = {
  color: 'var(--color-accent, var(--color-text-primary))',
  textDecoration: 'none',
  fontSize: 'var(--font-size-sm)',
  fontWeight: 'var(--font-weight-medium)',
  // ≥44px touch target (frontend-standards §3) without inflating an inline link.
  display: 'inline-flex',
  alignItems: 'center',
  minHeight: '2.75rem',
};

const articleStyle: CSSProperties = {
  // The rendered markdown blocks already carry their own rhythm.
  display: 'block',
};

const indexListStyle: CSSProperties = {
  listStyle: 'none',
  margin: 0,
  padding: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--space-2)',
};

const indexLinkStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  minHeight: '2.75rem',
  padding: 'var(--space-3) var(--space-4)',
  backgroundColor: 'var(--color-bg-muted)',
  borderRadius: 'var(--radius-md)',
  color: 'var(--color-text-primary)',
  textDecoration: 'none',
  fontWeight: 'var(--font-weight-medium)',
};

const indexHeadingStyle: CSSProperties = {
  // T47 Phase 4 — legal index heading → Poppins display.
  fontFamily: 'var(--font-family-display)',
  fontSize: 'var(--font-size-xl)',
  fontWeight: 'var(--font-weight-bold)',
  margin: 0,
  marginBottom: 'var(--space-2)',
};

const indexLeadStyle: CSSProperties = {
  margin: 0,
  marginBottom: 'var(--space-4)',
  color: 'var(--color-text-secondary)',
  lineHeight: 'var(--line-height-relaxed)',
};

/**
 * The shared public page frame: brand mark, a "Back to app" link, then the body.
 * Used by every legal page (the per-doc renderer and the delete-account wrapper)
 * so the public chrome stays consistent.
 */
export function LegalPageShell({
  testId,
  children,
}: {
  testId: string;
  children: ReactNode;
}): JSX.Element {
  return (
    <main style={pageStyle} data-testid={testId}>
      <div style={containerStyle}>
        <div style={topbarStyle}>
          <Logo size={40} showText data-testid="legal-logo" />
          {/* Back into the app. On the public surface (no session) this lands on
              the app entry, which the auth gate then routes to /login or /home. */}
          <Link to={routes.home} style={backLinkStyle} data-testid="legal-back-to-app">
            Back to app
          </Link>
        </div>
        {children}
      </div>
    </main>
  );
}

export interface LegalDocPageProps {
  /** Which document to render. */
  slug: LegalSlug;
}

const statusStyle: CSSProperties = {
  color: 'var(--color-text-secondary)',
  fontSize: 'var(--font-size-base)',
  lineHeight: 'var(--line-height-relaxed)',
};

type FetchState =
  | { phase: 'loading' }
  | { phase: 'ready'; content: string }
  | { phase: 'error' };

/**
 * Render one published legal document. The body is fetched at runtime from
 * `GET /api/legal/:slug` (an admin's Publish updates it with no app release) and
 * rendered through the dependency-free markdown renderer. The page never shows a
 * blank body: a loading state while the request is in flight, an error state on
 * failure (AC6).
 */
export function LegalDocPage({ slug }: LegalDocPageProps): JSX.Element {
  const [state, setState] = useState<FetchState>({ phase: 'loading' });

  useEffect(() => {
    let active = true;
    setState({ phase: 'loading' });
    fetchLegalDoc(slug)
      .then((doc) => {
        if (active) setState({ phase: 'ready', content: doc.content });
      })
      .catch(() => {
        if (active) setState({ phase: 'error' });
      });
    return () => {
      active = false;
    };
  }, [slug]);

  return (
    <LegalPageShell testId={`legal-${slug}`}>
      <article style={articleStyle} data-testid="legal-doc-body">
        {state.phase === 'loading' && (
          <p style={statusStyle} data-testid="legal-doc-loading">
            Loading {LEGAL_DOCS[slug].title}…
          </p>
        )}
        {state.phase === 'error' && (
          <p style={statusStyle} data-testid="legal-doc-error">
            We couldn’t load this document right now. Please check your connection and try
            again later.
          </p>
        )}
        {state.phase === 'ready' && renderLegalMarkdown(state.content)}
      </article>
    </LegalPageShell>
  );
}

/** The `/legal` index — links every public legal document. */
export function LegalIndexPage(): JSX.Element {
  const entries: { to: string; label: string }[] = [
    { to: routes.privacy, label: 'Privacy Policy' },
    { to: routes.terms, label: 'Terms of Service' },
    { to: routes.eula, label: 'End User License Agreement' },
    { to: routes.cookies, label: 'Cookie Policy' },
    { to: routes.deleteAccount, label: 'Delete your account' },
  ];
  return (
    <LegalPageShell testId="legal-index">
      <h1 style={indexHeadingStyle}>Legal &amp; privacy</h1>
      <p style={indexLeadStyle}>
        Shopista&apos;s policies and your data choices. These pages are public —
        you do not need to sign in to read them.
      </p>
      <ul style={indexListStyle}>
        {entries.map((e) => (
          <li key={e.to}>
            <Link to={e.to} style={indexLinkStyle} data-testid={`legal-index-link-${e.to.slice(1)}`}>
              {e.label}
            </Link>
          </li>
        ))}
      </ul>
    </LegalPageShell>
  );
}
